/**
 * Configuration data points — shared source of truth
 *
 * Builds the "Room Configuration" and "SlatWall Configuration" detail lists
 * (label / value / icon) from the wizard state. Consumed by:
 *   - ResultsVisualCard (the on-screen frosted overlay cards)
 *   - utils/pdf.ts (the PDF's project-details cards)
 * so the screen and the PDF show identical specifics.
 *
 * i18n: this module is not a component, so it resolves text through the live
 * i18next instance (see src/i18n/standalone.ts). Space types, calculation
 * modes, colors and orientations deliberately reuse the wizard/common keys
 * rather than getting results-namespace copies — the same value must read
 * identically on the wizard step where it was chosen and on the results page
 * and PDF that report it back.
 */

import {
  LayoutGrid,
  Grid3X3,
  Ruler,
  Palette,
  Move,
  DoorOpen,
  Hash,
  Square,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { EstimateOutput, WizardState, CeilingZone, WallConfig } from '../../types';
import { SPACE_TYPES } from '../../data/presets';
import { tr } from '../../i18n/standalone';

export interface DataPoint {
  Icon: LucideIcon;
  label: string;
  value: string;
}

/**
 * i18n keys for the three calculation modes, pointing at the wizard's own mode
 * titles so the results page and PDF name the mode exactly as the step that
 * chose it did. Unknown modes fall back to the raw value.
 */
const CALCULATION_MODE_KEYS: Record<string, string> = {
  CeilingOnly: 'wizard:mode.ceilingOnly.title',
  FullRoom: 'wizard:mode.fullRoom.title',
  IndividualWalls: 'wizard:mode.individualWalls.title',
};

/** Display name for a calculation mode in the active language. */
export function calculationModeLabel(mode: string): string {
  const key = CALCULATION_MODE_KEYS[mode];
  return key ? tr(key) : mode;
}

/**
 * Display name for a wizard space type in the active language.
 *
 * `fallback` covers a value the current wizard no longer offers — a saved
 * estimate from before a space type was retired. Without it those render as
 * "General", which is wrong rather than merely unhelpful.
 */
export function spaceTypeLabel(spaceType: string | undefined, fallback?: string): string {
  const match = SPACE_TYPES.find((t) => t.id === spaceType);
  if (match) return tr(`wizard:spaceTypes.${match.id}`);
  return fallback ?? tr('results:config.generalSpace');
}

/**
 * Translate a product color / panel orientation. Both are stable English enum
 * values ('White', 'Vertical') used as dictionary keys, and both may arrive as
 * a comma-joined list when walls differ, so each part is translated on its own.
 */
const translateEnumList = (value: string, ns: 'colors' | 'orientations'): string =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => tr(`common:${ns}.${part}`, { defaultValue: part }))
    .join(', ');

/** Product color(s) in the active language. Accepts a comma-joined list. */
export const colorLabel = (value: string): string => translateEnumList(value, 'colors');

/** Panel orientation(s) in the active language. Accepts a comma-joined list. */
export const orientationLabel = (value: string): string => translateEnumList(value, 'orientations');

function uniqueCeilingColors(zones: CeilingZone[] | undefined, fallback: string): string {
  if (!zones || zones.length === 0) return translateEnumList(fallback, 'colors');
  return translateEnumList([...new Set(zones.map((z) => z.color))].join(', '), 'colors');
}

export function buildRoomDataPoints(
  wizardState: WizardState,
  estimateResult: EstimateOutput,
): DataPoint[] {
  const points: DataPoint[] = [];
  const { applicationConfig, wallsConfig, ceilingConfig, calculationMode, individualWalls } = wizardState;
  const { roomDimensions } = estimateResult.input;

  points.push({
    Icon: Grid3X3,
    label: tr('results:config.application'),
    value: spaceTypeLabel(applicationConfig.spaceType),
  });
  points.push({
    Icon: LayoutGrid,
    label: tr('results:config.projectType'),
    value: calculationModeLabel(calculationMode),
  });

  // Size summary.
  //  - IndividualWalls (wall-by-wall): each wall/ceiling has its own dimensions,
  //    so a single "room size" is meaningless — show "Custom configuration".
  //  - CeilingOnly with >1 zone: no single dimension applies — show "Multiple Zones".
  //  - Otherwise: the L×W(×H) of the room/ceiling.
  let sizeLabel: string;
  let sizeValue: string;
  if (calculationMode === 'IndividualWalls') {
    sizeLabel = tr('results:config.roomSize');
    sizeValue = tr('results:config.customConfiguration');
  } else if (calculationMode === 'CeilingOnly') {
    sizeLabel = tr('results:config.ceilingSize');
    sizeValue = (ceilingConfig.zones && ceilingConfig.zones.length > 1)
      ? tr('results:config.multipleZones')
      : tr('results:config.sizeLW', { length: roomDimensions.length, width: roomDimensions.width });
  } else {
    sizeLabel = tr('results:config.roomSize');
    sizeValue = tr('results:config.sizeLWH', {
      length: roomDimensions.length,
      width: roomDimensions.width,
      height: roomDimensions.height,
    });
  }
  points.push({ Icon: Ruler, label: sizeLabel, value: sizeValue });

  // Total covered surface area — sum of W&CB and SlatWall coverage from the
  // BOM. Matches the formula in calculations/index.ts buildSummary().
  const bom = estimateResult.billOfMaterials;
  const coveredSqft =
    bom.wallAndCeilingBoard.reduce((sum, item) => sum + item.quantity * item.sqft, 0) +
    bom.slatwall.reduce((sum, item) => sum + item.quantity * item.sqft, 0);
  if (coveredSqft > 0) {
    points.push({
      Icon: Square,
      label: tr('results:config.squareFeet'),
      value: tr('results:config.squareFeetValue', { value: Math.round(coveredSqft).toLocaleString() }),
    });
  }

  if (calculationMode !== 'CeilingOnly' && calculationMode !== 'IndividualWalls') {
    points.push({
      Icon: Palette,
      label: tr('results:config.wallColor'),
      value: translateEnumList(wallsConfig.color, 'colors'),
    });
    points.push({
      Icon: Move,
      label: tr('results:config.panelOrientation'),
      value: translateEnumList(wallsConfig.orientation, 'orientations'),
    });
  }

  if (calculationMode === 'IndividualWalls' && individualWalls) {
    const enabled = individualWalls.filter((w: WallConfig) => w.enabled);
    points.push({
      Icon: Palette,
      label: tr('results:config.walls'),
      value: tr('results:config.wallsConfigured', { count: enabled.length }),
    });
    const colors = [...new Set(enabled.map((w: WallConfig) => w.color))].join(', ');
    points.push({
      Icon: Palette,
      label: tr('results:config.colors'),
      value: colors ? translateEnumList(colors, 'colors') : tr('common:notAvailable'),
    });
    const orientations = [...new Set(enabled.map((w: WallConfig) => w.orientation))].join(', ');
    points.push({
      Icon: Move,
      label: tr('results:config.orientations'),
      value: orientations ? translateEnumList(orientations, 'orientations') : tr('common:notAvailable'),
    });
  }

  if (ceilingConfig.include) {
    points.push({
      Icon: Palette,
      label: tr('results:config.ceiling'),
      value: uniqueCeilingColors(ceilingConfig.zones, ceilingConfig.color),
    });
  }

  // Garage doors
  // Two whole sentences per branch rather than a base string plus an appended
  // "on <wall>" clause: in French the location phrase moves, so a translator
  // has to own the word order of the complete value.
  if (calculationMode === 'FullRoom' && applicationConfig.garage?.enabled && applicationConfig.garage.numDoors > 0) {
    const g = applicationConfig.garage;
    const params = { count: g.numDoors, width: g.width, height: g.height };
    points.push({
      Icon: DoorOpen,
      label: tr('results:config.garageDoors'),
      value: g.onWallType !== 'None'
        ? tr('results:config.garageDoorsOnWall', {
            ...params,
            wall: g.onWallType === 'LengthWall'
              ? tr('results:config.lengthWall')
              : tr('results:config.widthWall'),
          })
        : tr('results:config.garageDoorsValue', params),
    });
  }
  if (calculationMode === 'IndividualWalls' && individualWalls) {
    const wallsWithGarage = individualWalls.filter((w: WallConfig) => w.enabled && w.garage.enabled && w.garage.numDoors > 0);
    for (const w of wallsWithGarage) {
      points.push({
        Icon: DoorOpen,
        label: tr('results:config.garageDoors'),
        // w.name is the user's own label for the wall — never translated.
        value: tr('results:config.garageDoorsOnWall', {
          count: w.garage.numDoors,
          width: w.garage.width,
          height: w.garage.height,
          wall: w.name,
        }),
      });
    }
  }

  return points;
}

export function buildSlatWallDataPoints(wizardState: WizardState): DataPoint[] {
  const points: DataPoint[] = [];
  const { calculationMode, wallsConfig, individualWalls } = wizardState;

  if (calculationMode !== 'IndividualWalls' && wallsConfig.slatWallNumPanels > 0) {
    points.push({
      Icon: Palette,
      label: tr('results:config.color'),
      value: translateEnumList(wallsConfig.slatWallColor, 'colors'),
    });
    points.push({
      Icon: Hash,
      label: tr('results:config.panels'),
      value: tr('results:config.panelRows', { count: wallsConfig.slatWallNumPanels }),
    });
    points.push({
      Icon: Ruler,
      label: tr('results:config.height'),
      value: tr('results:config.feet', { value: (wallsConfig.slatWallNumPanels * 0.5).toFixed(1) }),
    });
    points.push({
      Icon: Ruler,
      label: tr('results:config.startHeight'),
      value: tr('results:config.feetFromFloor', { value: wallsConfig.slatWallStartHeight }),
    });
  } else if (calculationMode === 'IndividualWalls' && individualWalls) {
    const wallsWithSlat = individualWalls.filter((w: WallConfig) => w.enabled && w.slatWall.enabled);
    points.push({
      Icon: Grid3X3,
      label: tr('results:config.wallsWithSlatWall'),
      value: String(wallsWithSlat.length),
    });
    const colors = [...new Set(wallsWithSlat.map((w: WallConfig) => w.slatWall.color))].join(', ');
    points.push({
      Icon: Palette,
      label: tr('results:config.colors'),
      value: colors ? translateEnumList(colors, 'colors') : tr('common:notAvailable'),
    });
  }

  return points;
}

export function hasSlatWall(wizardState: WizardState | null): boolean {
  if (!wizardState) return false;
  if (wizardState.calculationMode === 'IndividualWalls') {
    return !!wizardState.individualWalls?.some((w) => w.enabled && w.slatWall.enabled);
  }
  return wizardState.wallsConfig.slatWallNumPanels > 0;
}
